import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import {
  ArrowSquareOut, Calendar, Warning, Coins, Ticket, Trophy,
  ShieldCheck, Eye, ArrowLeft, Info,
} from "@phosphor-icons/react";
import api, { formatGBPDecimal, formatNumber, categoryLabel } from "../lib/api";
import {
  publicPrizeCell,
  publicCashAltCell,
  publicIncludedCashCell,
  filterExplanationsForUnknownPrize,
} from "../lib/displayValue";
import { totalTicketCapInfo } from "../lib/budgetOdds";
import {
  botbDisplayDateFallback,
  isBotbRecord,
  isBotbSkillBased,
} from "../lib/botbDisplay";
import ScoreBreakdown from "../components/ScoreBreakdown";
import SEO from "../components/SEO";
import PrizeImage from "../components/PrizeImage";
import CompetitionBudgetModule from "../components/CompetitionBudgetModule";
import AffiliateLinkLabel from "../components/AffiliateLinkLabel";
import PremiumLiveOdds from "../components/PremiumLiveOdds";
import PremiumYourOdds from "../components/PremiumYourOdds";
import PremiumOddsHistory from "../components/PremiumOddsHistory";
import PremiumPredictions from "../components/PremiumPredictions";
import PremiumAlerts from "../components/PremiumAlerts";
import { affiliateDisclosureFor } from "../lib/affiliateDisclosure";
import { publicImageUrlForCompetition } from "../lib/publicImage";
import {
  currentTicketPrice,
  freeOrPostalEntryText,
  freeOrPostalEntryUrl,
  hasFreeOrPostalEntryMethod,
  publicClosingCountdown,
  publicOddsState,
} from "../lib/publicCompetitionContract";
import {
  fetchCatalogueDetail,
  isCatalogueAbort,
} from "../lib/publicCatalogue";
import {
  catalogueDetailSnapshotFromNavigation,
  clearCatalogueDetailNavigation,
} from "../lib/catalogueDetailNavigation";

const Stat = ({ icon: Icon, label, value, sub, testId, ariaLabel }) => (
  <div className="rounded-md border border-slate-200 p-4 bg-white" data-testid={testId}>
    <div className="flex items-center gap-2 overline text-[var(--cc-text-dark-muted)] mb-2">
      <Icon size={12} weight="fill" /> {label}
    </div>
    <div className="font-mono-num text-xl font-semibold text-[var(--cc-text-dark)]" aria-label={ariaLabel}>{value}</div>
    {sub && <div className="text-xs text-[var(--cc-text-dark-muted)] mt-1">{sub}</div>}
  </div>
);

const RISK_FLAG_LABELS = {
  max_tickets_missing: "Maximum ticket count not published",
  max_tickets_missing_value_uncertain: "Value estimate has reduced confidence (ticket cap missing)",
  prize_value_missing: "Prize value estimate missing",
  free_entry_unclear: "Free entry route not clearly published",
  extension_policy_unclear: "Extension policy not clearly published",
  draw_date_missing: "Draw date not published",
  operator_unknown: "Operator information missing",
};

const PUBLIC_MONTHS = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

const parsePublicDateValue = (value) => {
  if (!value) return null;
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;
  const match = String(value).match(
    /\b(?:mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)?\s*(\d{1,2})(?:st|nd|rd|th)?\s+(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(20\d{2})/i
  );
  if (!match) return null;
  const month = PUBLIC_MONTHS[match[2].toLowerCase()];
  if (month == null) return null;
  const parsed = new Date(Number(match[3]), month, Number(match[1]));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const publicDateLabel = (...values) => {
  for (const value of values) {
    const parsed = parsePublicDateValue(value);
    if (parsed) {
      return parsed.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  }
  const fallback = values.find((value) => String(value || "").trim());
  return fallback ? String(fallback) : "TBA";
};

const isCalculatedOddsExplanation = (value) => (
  /\bodds are 1 in\b|\bcalculable odds\b/i.test(String(value || ""))
);

const competitionDetailFailure = (requestError) => {
  const status = Number(requestError?.response?.status || 0);
  if (status === 404) {
    return {
      title: "Competition not found",
      description: "The requested public competition could not be found.",
      message: "This competition could not be found or is no longer available.",
      action: "Back to competitions",
    };
  }
  if (status === 409
    && requestError?.response?.data?.detail?.code === "catalogue_snapshot_changed") {
    return {
      title: "Catalogue updated",
      description: "The catalogue has changed since this competition link was opened.",
      message: "View the latest competitions to open the current record.",
      action: "View latest competitions",
    };
  }
  return {
    title: "Competition temporarily unavailable",
    description: "Competition Checker could not load this public record right now.",
    message: "Please return to the competitions page and try again.",
    action: "Back to competitions",
  };
};

export default function CompetitionDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const originatingSnapshotId = useMemo(() => (
    catalogueDetailSnapshotFromNavigation({ state: location.state, slug })
  ), [location.state, slug]);
  const requestGeneration = useRef(0);
  const [state, setState] = useState({ comp: null, loading: true, error: null });
  const { comp, loading, error } = state;
  const [closingNow, setClosingNow] = useState(() => Date.now());

  useEffect(() => {
    setClosingNow(Date.now());
    if (!publicClosingCountdown(comp)) return undefined;
    const timer = setInterval(() => setClosingNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [comp]);

  useLayoutEffect(() => {
    clearCatalogueDetailNavigation({ state: location.state, slug });
  }, [location.state, slug]);

  useEffect(() => {
    const generation = requestGeneration.current + 1;
    requestGeneration.current = generation;
    const controller = new AbortController();
    setState({ comp: null, loading: true, error: null });
    fetchCatalogueDetail({
      slug,
      snapshotId: originatingSnapshotId,
      allowSnapshotRefresh: true,
      signal: controller.signal,
    }).then(({ detail }) => {
      if (!controller.signal.aborted && requestGeneration.current === generation) {
        setState({ comp: detail.item, loading: false, error: null });
      }
    }, (requestError) => {
      if (
        controller.signal.aborted
        || requestGeneration.current !== generation
        || isCatalogueAbort(requestError)
      ) return;
      setState({
        comp: null,
        loading: false,
        error: competitionDetailFailure(requestError),
      });
    });
    return () => controller.abort();
  }, [originatingSnapshotId, slug]);

  if (loading) return (
    <div className="max-w-7xl mx-auto p-12 text-[var(--cc-text-muted)]">
      <SEO
        title="Loading competition"
        description="Competition Checker is loading this public competition record."
        path={`/competition/${slug}`}
        noIndex
      />
      Loading...
    </div>
  );
  if (error || !comp) {
    const failure = error || competitionDetailFailure(null);
    return (
      <div className="max-w-7xl mx-auto p-12 text-center">
        <SEO
          title={failure.title}
          description={failure.description}
          path={`/competition/${slug}`}
          noIndex
        />
        <h1 className="font-display text-3xl font-semibold">{failure.title}</h1>
        <p className="mt-3 text-[var(--cc-text-muted)]">{failure.message}</p>
        <Link to="/competitions" className="mt-4 inline-block text-[var(--cc-gold)] underline">{failure.action}</Link>
      </div>
    );
  }

  const s = comp.scores;
  const closingCountdown = publicClosingCountdown(comp, closingNow);
  const prizeCell = publicPrizeCell(comp);
  const includedCashCell = publicIncludedCashCell(comp);
  const cashAltCell = publicCashAltCell(comp);

  // ---------- SEO metadata (iter 8) -----------------------------------------
  // Safe-image gate: only feed OG / Twitter / JSON-LD a real image when the
  // asset resolver has cleared it. Pending / placeholder / brand_fallback /
  // mismatch_blocked never leaks into a social preview — the brand fallback
  // image is used.
  const assetOk =
    comp.asset_resolution_status === "exact_match" ||
    comp.asset_resolution_status === "close_match" ||
    comp.asset_resolution_status === "generated_render" ||
    comp.asset_resolution_status === "source_match";
  const safeImageUrl = assetOk ? publicImageUrlForCompetition(comp) : null;
  const prizeName = comp.prize_display_name || comp.prize_name || comp.title;
  const seoTitle = `${prizeName} odds, value and transparency`;
  const seoDescription =
    `Check estimated odds, ticket price, prize value, free-entry clarity and CompCheck Score for "${prizeName}"` +
    (comp.operator?.name ? ` by ${comp.operator.name}` : "") +
    " before you enter.";

  // This is an editorial comparison page, not an ecommerce product listing.
  const detailUrl = `https://competitionchecker.com/competition/${comp.slug}`;
  const breadcrumbId = `${detailUrl}#breadcrumb`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${detailUrl}#webpage`,
        url: detailUrl,
        name: seoTitle,
        description: seoDescription,
        isPartOf: { "@id": "https://competitionchecker.com/#website" },
        breadcrumb: { "@id": breadcrumbId },
        about: {
          "@type": "Thing",
          name: prizeName,
          description: comp.prize_description || seoDescription,
          ...(safeImageUrl ? { image: safeImageUrl } : {}),
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbId,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Competitions",
            item: "https://competitionchecker.com/competitions",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: prizeName,
            item: detailUrl,
          },
        ],
      },
    ],
  };
  // -------------------------------------------------------------------------

  const ctaLabel = "Visit operator site";
  const ctaTarget = comp.cta_target || "competition";
  const ctaHref = comp.cta_destination_url || comp.affiliate_url || comp.competition_url || "";
  const affiliateDisclosure = affiliateDisclosureFor(comp, ctaHref);
  const ctaDisabled = ctaTarget === "none" || !ctaHref;
  const handleOutbound = (event) => {
    if (ctaDisabled) {
      event.preventDefault();
      return;
    }
    try {
      Promise.resolve(api.post(`/competitions/${comp.id}/click`)).catch(() => {});
    } catch {
      // The anchor must still navigate when tracking fails synchronously.
    }
  };
  const dataStatus = comp.competition_data_status;
  const isDemoListing =
    comp.is_demo_seed || dataStatus === "seed_demo" || dataStatus === "source_pending_review";
  const isUnvalidated =
    !comp.is_deep_link_validated && ctaTarget !== "affiliate";
  const isBotb = isBotbRecord(comp);
  const isBotbSkill = isBotbSkillBased(comp);
  const isMckinney = (
    String(comp.operator_key || "").toLowerCase() === "mckinney"
    || String(comp.operator?.name || "").toLowerCase() === "mckinney competitions"
  );
  const isMckinneyAwarenessOnly = isMckinney && (
    comp.mckinney_awareness_only === true
    || comp.mckinney_standard_odds_supported === false
  );
  const isLlf = (
    String(comp.operator_key || "").toLowerCase() === "llf"
    || String(comp.operator?.name || "").toLowerCase() === "llf games"
  );
  const isLlfAwarenessOnly = isLlf && (
    comp.llf_awareness_only === true
    || comp.llf_standard_odds_supported === false
  );
  const isKilted = (
    String(comp.operator_key || "").toLowerCase() === "kilted"
    || String(comp.operator?.name || "").toLowerCase() === "kilted competitions"
  );
  const isKiltedAwarenessOnly = isKilted && (
    comp.kilted_awareness_only === true
    || comp.kilted_standard_odds_supported === false
  );
  const isUkcc = (
    String(comp.operator_key || "").toLowerCase() === "ukcc"
    || String(comp.operator?.name || "").toLowerCase() === "ukcc competitions"
    || String(comp.operator?.name || "").toLowerCase() === "uk carp competitions"
  );
  const isUkccAwarenessOnly = isUkcc && (
    comp.ukcc_awareness_only === true
    || comp.ukcc_standard_odds_supported === false
  );
  const isBounty = (
    String(comp.operator_key || "").toLowerCase() === "bounty"
    || String(comp.operator?.name || "").toLowerCase() === "bounty competitions"
  );
  const isBountyAwarenessOnly = isBounty && (
    comp.bounty_awareness_only === true
    || comp.bounty_standard_odds_supported === false
  );
  const isStorm = (
    String(comp.operator_key || "").toLowerCase() === "storm"
    || String(comp.operator?.name || "").toLowerCase() === "storm competitions"
  );
  const isStormAwarenessOnly = isStorm && (
    comp.storm_awareness_only === true
    || comp.storm_standard_odds_supported === false
  );
  const isRedline = (
    String(comp.operator_key || "").toLowerCase() === "redline"
    || String(comp.operator?.name || "").toLowerCase() === "redline competitions"
  );
  const isRedlineAwarenessOnly = isRedline && (
    comp.redline_awareness_only === true
    || comp.redline_standard_odds_supported === false
  );
  const isPrizeParadise = (
    String(comp.operator_key || "").toLowerCase() === "prizeparadise"
    || String(comp.operator?.name || "").toLowerCase() === "prize paradise"
  );
  const isPrizeParadiseAwarenessOnly = isPrizeParadise && (
    comp.prizeparadise_awareness_only === true
    || comp.prizeparadise_standard_odds_supported === false
  );
  const isCaddy = (
    String(comp.operator_key || "").toLowerCase() === "caddy"
    || ["caddy comps", "caddy competitions"].includes(
      String(comp.operator?.name || comp.operator_name || "").toLowerCase()
    )
  );
  const isCaddyAwarenessOnly = isCaddy && (
    comp.caddy_awareness_only === true
    || comp.caddy_standard_odds_supported === false
  );
  const isHotComps = (
    String(comp.operator_key || "").toLowerCase() === "hotcomps"
    || ["hot comps", "hot comps competitions"].includes(
      String(comp.operator?.name || comp.operator_name || "").toLowerCase()
    )
  );
  const isHotCompsAwarenessOnly = isHotComps && (
    comp.hotcomps_awareness_only === true
    || comp.hotcomps_standard_odds_supported === false
  );
  const isApex = (
    String(comp.operator_key || "").toLowerCase() === "apex"
    || String(comp.operator?.name || "").toLowerCase() === "apex competitions"
  );
  const isApexAwarenessOnly = isApex && (
    comp.apex_awareness_only === true
    || comp.apex_standard_odds_supported === false
  );
  const isJammy = (
    String(comp.operator_key || "").toLowerCase() === "jammy"
    || ["jammy", "jammy competitions"].includes(
      String(comp.operator?.name || comp.operator_name || "").toLowerCase()
    )
  );
  const isJammyAwarenessOnly = isJammy && (
    comp.jammy_awareness_only === true
    || comp.jammy_standard_odds_supported === false
  );
  const isPawsome = (
    String(comp.operator_key || "").toLowerCase() === "pawsome"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "pawsome competitions"
  );
  const isPawsomeAwarenessOnly = isPawsome && (
    comp.pawsome_awareness_only === true
    || comp.pawsome_standard_odds_supported === false
  );
  const isLincolnshire = (
    String(comp.operator_key || "").toLowerCase() === "lincolnshire"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "lincolnshire competitions"
  );
  const isLincolnshireAwarenessOnly = isLincolnshire && (
    comp.lincolnshire_awareness_only === true
    || comp.lincolnshire_standard_odds_supported === false
  );
  const isDrift = (
    String(comp.operator_key || "").toLowerCase() === "drift"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "drift competitions"
  );
  const isDriftAwarenessOnly = isDrift && (
    comp.drift_awareness_only === true
    || comp.drift_standard_odds_supported === false
  );
  const isPalladium = (
    String(comp.operator_key || "").toLowerCase() === "palladium"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "palladium competitions"
  );
  const isPalladiumAwarenessOnly = isPalladium && (
    comp.palladium_awareness_only === true
    || comp.palladium_standard_odds_supported === false
  );
  const isPristine = (
    String(comp.operator_key || "").toLowerCase() === "pristine"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "pristine competitions"
  );
  const isPristineAwarenessOnly = isPristine && (
    comp.pristine_awareness_only === true
    || comp.pristine_standard_odds_supported === false
  );
  const isThatPrizeGuy = (
    String(comp.operator_key || "").toLowerCase() === "thatprizeguy"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "that prize guy"
  );
  const isThatPrizeGuyAwarenessOnly = isThatPrizeGuy && (
    comp.thatprizeguy_awareness_only === true
    || comp.thatprizeguy_standard_odds_supported === false
  );
  const isBright = (
    String(comp.operator_key || "").toLowerCase() === "bright"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "bright competitions"
  );
  const isBrightAwarenessOnly = isBright && (
    comp.bright_awareness_only === true
    || comp.bright_standard_odds_supported === false
  );
  const isSalexmo = (
    String(comp.operator_key || "").toLowerCase() === "salexmo"
    || String(comp.operator?.name || comp.operator_name || "").toLowerCase()
      === "salexmo"
  );
  const isSalexmoAwarenessOnly = isSalexmo && (
    comp.salexmo_awareness_only === true
    || comp.salexmo_standard_odds_supported === false
  );
  const isClick = (
    String(comp.operator_key || "").toLowerCase() === "click"
    || String(comp.operator?.name || "").toLowerCase() === "click competitions"
  );
  const isClickAwarenessOnly = isClick && comp.click_awareness_only === true;
  const capInfo = totalTicketCapInfo(comp);
  const oddsState = publicOddsState(comp, capInfo);
  const detailOddsReady = oddsState.isReady;
  const displayTicketPrice = currentTicketPrice(comp);
  const oddsUnavailableReason = oddsState.reason;
  const displayOdds = oddsState.displayText;
  const explicitOddsBlock = comp.odds_ready === false;
  const numbersSummary = explicitOddsBlock
    ? `${oddsUnavailableReason || "Trusted standard odds are unavailable for this listing"}.`
    : comp.summary;
  const numbersExplanations = filterExplanationsForUnknownPrize(
    s.explanations,
    comp,
  ).filter((explanation) => (
    !explicitOddsBlock || !isCalculatedOddsExplanation(explanation)
  ));
  const displayDate = publicDateLabel(
    comp.parsed_date,
    comp.draw_date,
    comp.closing_date,
    botbDisplayDateFallback(comp),
  );

  return (
    <div className="bg-[var(--cc-navy)] min-h-screen">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/competition/${comp.slug}`}
        image={safeImageUrl}
        imageAlt={comp.image_alt_text || prizeName}
        ogType="article"
        jsonLd={jsonLd}
        jsonLdId={`comp-${comp.slug}`}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 text-xs text-white/60">
          <Link to="/competitions" className="inline-flex items-center gap-2 hover:text-white" data-testid="back-to-list">
            <ArrowLeft size={14} /> Competitions
          </Link>
          <span aria-hidden="true">/</span>
          <span>{prizeName}</span>
        </nav>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 items-start">
          {/* Left: image + main info */}
          <div>
            <div className="relative rounded-lg overflow-hidden aspect-[16/10] bg-slate-800">
              <PrizeImage comp={comp} variant="detail" />
              <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                <span className="bg-[var(--cc-navy)] text-white px-2.5 py-1 text-[11px] rounded-full font-semibold">
                  {categoryLabel(comp.category)}
                </span>
                {isBotbSkill && (
                  <span
                    className="bg-[var(--cc-navy)] text-white px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-botb-skill-badge"
                  >
                    Skill-based
                  </span>
                )}
                {isMckinneyAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-mckinney-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isLlfAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-llf-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isKiltedAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-kilted-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isUkccAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-ukcc-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isBountyAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-bounty-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isStormAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-storm-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isRedlineAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-redline-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isPrizeParadiseAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-prizeparadise-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isCaddyAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-caddy-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isHotCompsAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-hotcomps-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isApexAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-apex-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isJammyAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-jammy-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isPawsomeAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-pawsome-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isLincolnshireAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-lincolnshire-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isDriftAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-drift-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isPalladiumAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-palladium-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isPristineAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-pristine-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isThatPrizeGuyAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-thatprizeguy-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isBrightAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-bright-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isSalexmoAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-salexmo-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {isClickAwarenessOnly && (
                  <span
                    className="bg-amber-100 text-amber-900 px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-click-awareness-badge"
                  >
                    Awareness only
                  </span>
                )}
                {closingCountdown && (
                  <time
                    className="bg-amber-500 text-[var(--cc-navy)] px-2.5 py-1 text-[11px] rounded-full font-semibold"
                    data-testid="detail-entry-closing-countdown"
                    dateTime={closingCountdown.dateTime}
                    title={`Entry closes ${closingCountdown.ukTime} (UK time)`}
                  >
                    {closingCountdown.label}
                  </time>
                )}
              </div>
            </div>

            <div className="mt-8">
              <div className="overline text-[var(--cc-gold)] mb-2">
                {comp.operator?.slug ? (
                  <Link className="hover:underline" to={`/operators/${comp.operator.slug}`}>
                    {comp.operator.name}
                  </Link>
                ) : comp.operator?.name}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-white">
                {comp.title}
              </h1>
              <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                {comp.prize_description}
              </p>
            </div>

            {/* Stats grid */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat
                icon={Ticket}
                label="Current live ticket price"
                value={formatGBPDecimal(displayTicketPrice)}
                sub={comp.promo_price_detected ? "Promotion detected" : null}
                testId="stat-ticket-price"
              />
              <Stat
                icon={Trophy}
                label={prizeCell.label}
                value={prizeCell.value}
                sub={
                  prizeCell.source === "title"
                    ? "From competition title"
                    : null
                }
                testId="stat-prize-value"
              />
              {includedCashCell.present && (
                <Stat
                  icon={Coins}
                  label={includedCashCell.label}
                  value={includedCashCell.value}
                  testId="stat-included-cash"
                />
              )}
              {cashAltCell.present && (
                <Stat
                  icon={Coins}
                  label={cashAltCell.label}
                  value={cashAltCell.value}
                  testId="stat-cash-alt"
                />
              )}
              <Stat icon={Ticket} label="Max tickets" value={comp.max_tickets ? formatNumber(comp.max_tickets) : "Unknown"} testId="stat-max-tickets" />
              {comp.current_entries != null && (
                <Stat icon={Ticket} label="Current entries" value={formatNumber(comp.current_entries)} testId="stat-current-entries" />
              )}
              {comp.remaining_stock != null && (
                <Stat icon={Ticket} label="Remaining tickets" value={formatNumber(comp.remaining_stock)} testId="stat-remaining-stock" />
              )}
              {(comp.per_user_limit ?? comp.per_user_ticket_limit) != null && (
                <Stat icon={Ticket} label="Per-user limit" value={formatNumber(comp.per_user_limit ?? comp.per_user_ticket_limit)} testId="stat-per-user-limit" />
              )}
              <Stat icon={Calendar} label="Draw date" value={displayDate} testId="stat-draw-date" />
              <Stat
                icon={Eye}
                label="Estimated odds"
                value={displayOdds}
                sub={!detailOddsReady ? oddsUnavailableReason : s.value_ratio ? `Value ratio: ${s.value_ratio}×` : null}
                testId="stat-odds"
                ariaLabel={oddsState.ariaLabel}
              />
            </div>

            <PremiumLiveOdds competitionId={comp.id} />
            <PremiumYourOdds competitionId={comp.id} />
            <PremiumOddsHistory competitionId={comp.id} />
            <PremiumPredictions competitionId={comp.id} />
            <PremiumAlerts competitionId={comp.id} />

            {/* Budget odds module — Iter 20.21.d / Message 763 */}
            <div className="mt-8">
              <CompetitionBudgetModule comp={comp} />
            </div>

            {isMckinneyAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="mckinney-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}

            {isLlfAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="llf-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This LLF mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isKiltedAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="kilted-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Kilted mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isUkccAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="ukcc-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This UKCC competition does not expose the complete trusted evidence needed for standard odds, so Competition Checker does not calculate or invent them.
                </p>
              </div>
            )}
            {isBountyAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="bounty-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Bounty mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isStormAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="storm-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Storm mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isRedlineAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="redline-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Redline mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isPrizeParadiseAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="prizeparadise-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Prize Paradise mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isCaddyAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="caddy-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Caddy mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isHotCompsAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="hotcomps-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Hot Comps mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isApexAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="apex-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Apex mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isJammyAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="jammy-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This JAMMY mechanic does not use the standard single-prize odds model, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isPawsomeAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="pawsome-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Pawsome mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}

            {isLincolnshireAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="lincolnshire-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Lincolnshire mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}

            {isDriftAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="drift-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Drift mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isPalladiumAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="palladium-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  Palladium exposes the current competition and trusted prize details, but not enough competition-wide data for reliable standard odds. We keep it visible without estimating odds.
                </p>
              </div>
            )}
            {isPristineAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="pristine-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  Pristine exposes the current competition and trusted prize details, but not enough competition-wide data for reliable standard odds. We keep it visible without estimating odds.
                </p>
              </div>
            )}

            {isThatPrizeGuyAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="thatprizeguy-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This That Prize Guy mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}

            {isBrightAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="bright-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Bright mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}
            {isSalexmoAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="salexmo-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Salexmo mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}

            {isClickAwarenessOnly && (
              <div
                className="mt-6 rounded-lg border border-amber-300/70 bg-amber-50 p-5 text-amber-950"
                data-testid="click-awareness-explanation"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill" /> Awareness-only competition</div>
                <p className="text-sm leading-relaxed">
                  This Click mechanic does not have a trusted standard-odds contract, so Competition Checker does not calculate or invent odds for it.
                </p>
              </div>
            )}

            {(comp.mckinney_date_is_maximum_possible === true || comp.mckinney_date_brought_forward_when_sold_out === true) && (
              <div
                className="mt-6 rounded-lg border border-sky-200 bg-sky-50 p-5 text-sky-950"
                data-testid="mckinney-draw-date-caveat"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Calendar size={12} weight="fill" /> Draw-date note</div>
                <p className="text-sm leading-relaxed">
                  The displayed date is the maximum possible draw date. The operator may bring the draw forward if tickets sell out earlier; check the official competition terms before entering.
                </p>
              </div>
            )}

            {(comp.llf_date_is_maximum_possible === true || comp.llf_date_brought_forward_when_sold_out === true) && (
              <div
                className="mt-6 rounded-lg border border-sky-200 bg-sky-50 p-5 text-sky-950"
                data-testid="llf-draw-date-caveat"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Calendar size={12} weight="fill" /> Draw-date note</div>
                <p className="text-sm leading-relaxed">
                  The displayed date is the operator&apos;s stated latest draw date. Check the official LLF competition terms before entering because the draw timing may depend on the competition mechanic or sell-out conditions.
                </p>
              </div>
            )}
            {(comp.kilted_date_is_maximum_possible === true || comp.kilted_date_brought_forward_when_sold_out === true) && (
              <div
                className="mt-6 rounded-lg border border-sky-200 bg-sky-50 p-5 text-sky-950"
                data-testid="kilted-draw-date-caveat"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Calendar size={12} weight="fill" /> Draw-date note</div>
                <p className="text-sm leading-relaxed">
                  The displayed date is the operator&apos;s stated latest draw date. Check the official Kilted competition terms before entering because the draw timing may depend on the competition mechanic or sell-out conditions.
                </p>
              </div>
            )}
            {(comp.ukcc_date_is_maximum_possible === true || comp.ukcc_date_brought_forward_when_sold_out === true) && (
              <div
                className="mt-6 rounded-lg border border-sky-200 bg-sky-50 p-5 text-sky-950"
                data-testid="ukcc-draw-date-caveat"
              >
                <div className="overline mb-2 inline-flex items-center gap-1.5"><Calendar size={12} weight="fill" /> Draw-date note</div>
                <p className="text-sm leading-relaxed">
                  The displayed date is the operator&apos;s stated draw date. Check the official UKCC competition terms before entering because timing may depend on sell-out conditions.
                </p>
              </div>
            )}

            {/* What the numbers say */}
            <div className="mt-8 rounded-lg bg-white p-6 border border-slate-200" data-testid="numbers-summary">
              <div className="overline text-[var(--cc-text-dark-muted)] mb-2 inline-flex items-center gap-1.5"><Info size={12} weight="fill"/> What the numbers say</div>
              <p className="text-[var(--cc-text-dark)] leading-relaxed">{numbersSummary}</p>
              <ul className="mt-4 space-y-2 text-sm text-[var(--cc-text-dark-muted)]">
                {numbersExplanations.map((e, i) => (<li key={i} className="flex gap-2"><span className="text-[var(--cc-gold)]">•</span><span>{e}</span></li>))}
              </ul>
            </div>

            <nav className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm" aria-label="Competition analysis help">
              <Link className="text-[var(--cc-gold)] hover:underline" to="/guides/how-competition-odds-are-calculated">
                How these odds are calculated
              </Link>
              <Link className="text-[var(--cc-gold)] hover:underline" to="/how-we-score">
                Scoring methodology
              </Link>
              <Link className="text-[var(--cc-gold)] hover:underline" to="/data-accuracy">
                Data accuracy
              </Link>
            </nav>

            {/* Transparency details */}
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {hasFreeOrPostalEntryMethod(comp) && (
                <div className="rounded-lg bg-white p-5 border border-slate-200" data-testid="free-postal-entry-method">
                  <div className="overline text-[var(--cc-text-dark-muted)] mb-2 inline-flex items-center gap-1.5"><ShieldCheck size={12} weight="fill"/> Free/postal entry method available</div>
                  <p className="text-sm text-[var(--cc-text-dark)]">
                    {freeOrPostalEntryUrl(comp) ? (
                      <a href={freeOrPostalEntryUrl(comp)} target="_blank" rel="nofollow noopener noreferrer" className="text-[var(--cc-bronze)] underline">
                        {freeOrPostalEntryText(comp) || "View the operator's entry method"}
                      </a>
                    ) : (freeOrPostalEntryText(comp) || "Free/postal entry method available")}
                  </p>
                </div>
              )}
              <div className="rounded-lg bg-white p-5 border border-slate-200">
                <div className="overline text-[var(--cc-text-dark-muted)] mb-2 inline-flex items-center gap-1.5"><Calendar size={12} weight="fill"/> Extension policy</div>
                <p className="text-sm text-[var(--cc-text-dark)]">
                  {comp.extension_policy_summary || "Extension policy is not clearly documented."}
                </p>
              </div>
            </div>

            {/* Risk flags */}
            {s.risk_flags.length > 0 && (
              <div className="mt-6 rounded-lg p-5 bg-red-50 border border-red-200" data-testid="risk-flags">
                <div className="overline text-red-800 mb-3 inline-flex items-center gap-1.5"><Warning size={12} weight="fill"/> Risk flags</div>
                <ul className="space-y-1.5">
                  {s.risk_flags.map((f) => (
                    <li key={f} className="text-sm text-red-900 flex gap-2">
                      <span>•</span>
                      <span>{RISK_FLAG_LABELS[f] || f.replace(/_/g, " ").replace("operator:", "Operator: ")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right: scorecard sticky */}
          <aside className="lg:sticky lg:top-24 space-y-4">
            <div className="rounded-lg bg-white border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[11px] font-semibold tracking-wide text-[var(--cc-text-dark-muted)]">CompCheck Score</div>
                  <div className="font-display text-5xl font-semibold mt-1 text-[var(--cc-text-dark)] font-mono-num" data-testid="overall-score">
                    {s.overall_compcheck_score}
                    <span className="text-2xl text-[var(--cc-text-dark-muted)]">/100</span>
                  </div>
                  <div
                    data-testid="score-band-label"
                    className="mt-1 inline-block text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{
                      background: s.score_band === "Strong" ? "rgba(16,185,129,0.12)" :
                                  s.score_band === "Good" ? "rgba(16,185,129,0.08)" :
                                  s.score_band === "Mixed" ? "rgba(245,158,11,0.12)" :
                                  "rgba(220,38,38,0.12)",
                      color: s.score_band === "Strong" || s.score_band === "Good" ? "#047857" :
                             s.score_band === "Mixed" ? "#92400e" : "#991b1b",
                    }}
                  >
                    {s.score_band}
                  </div>
                </div>
                <div className="text-right">
                  <div className="overline text-[var(--cc-text-dark-muted)]">Odds</div>
                  <div className="font-mono-num text-lg font-semibold text-[var(--cc-text-dark)]" aria-label={oddsState.ariaLabel}>{displayOdds}</div>
                  {!detailOddsReady && (
                    <div
                      className="text-[10px] text-[var(--cc-text-dark-muted)] max-w-[120px]"
                      data-testid={isBotb ? "detail-botb-odds-reason" : "detail-odds-reason"}
                    >
                      {oddsUnavailableReason}
                    </div>
                  )}
                </div>
              </div>
              <div className="gold-rule mb-5" />
              {comp.score_summary && (
                <div className="mb-5 pb-5 border-b border-slate-200" data-testid="what-this-score-means">
                  <div className="overline text-[var(--cc-text-dark-muted)] mb-2">What this score means</div>
                  <p className="text-xs text-[var(--cc-text-dark)] leading-relaxed">{comp.score_summary}</p>
                </div>
              )}
              <ScoreBreakdown scores={s} />
              <a
                href={ctaDisabled ? undefined : ctaHref}
                target="_blank"
                rel={affiliateDisclosure.isAffiliateLink ? "nofollow sponsored noopener noreferrer" : "nofollow noopener noreferrer"}
                onClick={handleOutbound}
                data-testid="view-competition-btn"
                aria-disabled={ctaDisabled ? "true" : undefined}
                tabIndex={ctaDisabled ? -1 : undefined}
                className={`bg-black text-[var(--cc-gold)] font-display font-semibold border border-[var(--cc-gold)]/60 w-full mt-6 px-3 py-3.5 rounded inline-flex items-center justify-center gap-2 ${
                  ctaDisabled ? "opacity-60 cursor-not-allowed pointer-events-none" : ""
                }`}
              >
                {ctaLabel} <ArrowSquareOut size={16} weight="bold" />
              </a>
              <div className="mt-3 flex justify-center">
                <AffiliateLinkLabel record={comp} destinationUrl={ctaHref} />
              </div>
              <p className="mt-3 text-[11px] text-[var(--cc-text-dark-muted)] leading-relaxed text-center" data-testid="cta-disclaimer">
                {affiliateDisclosure.isAffiliateLink
                  ? affiliateDisclosure.disclosure
                  : ctaTarget === "operator" && !comp.is_deep_link_validated
                  ? "Source competition link is not yet validated — this opens the operator's homepage."
                  : ctaTarget === "none"
                  ? "Source URL is pending review. Outbound link disabled until validated."
                  : "We do not sell tickets. You will be taken to the operator's site."}
              </p>
              {(isDemoListing || isUnvalidated) && (
                <div
                  data-testid="data-integrity-banner"
                  className="mt-4 rounded-md border border-amber-300/60 bg-amber-50 text-amber-900 px-3 py-2 text-[11px] leading-relaxed"
                >
                  {isDemoListing ? (
                    <>
                      <strong>Demo listing.</strong>{" "}
                      This competition is currently displayed for preview only — its source URL has
                      not been validated as a live deep link.
                    </>
                  ) : (
                    <>
                      <strong>Source pending verification.</strong>{" "}
                      Treat this listing as preliminary until an admin verifies the source.
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-white/10 p-5 bg-white/[0.02]">
              <div className="overline text-[var(--cc-gold)] mb-2">Responsible participation</div>
              <p className="text-xs text-[var(--cc-text-muted)] leading-relaxed">
                Only spend what you can comfortably afford. Most UK prize competitions
                are required to offer a free entry route — check the operator&apos;s terms.
              </p>
            </div>

            {comp.operator && (
              <Link
                to={`/operators/${comp.operator.slug}`}
                data-testid="operator-link"
                className="block rounded-lg border border-white/10 p-5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <div className="overline text-white/40 mb-1">Operator</div>
                <div className="font-display text-lg font-semibold text-white">{comp.operator.name}</div>
                <p className="text-xs text-[var(--cc-text-muted)] mt-2 line-clamp-2">{comp.operator.trust_summary}</p>
              </Link>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
